import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';

import { StatusToggleButton } from './StatusToggleButton';

import { normalizeStatus } from './index';

import type { LocationDetail } from '@/service/types/dockDoorTypes';

interface DockDoorTableProps {
  locations: LocationDetail[];
  updatingLocation: string | null;
  hasPermission: boolean;
  onToggleStatus: (location: LocationDetail) => void;
}

// Matches Xamarin DataTrigger TextColor bindings
function getStatusColor(status: string): string {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'Available':
      return 'text-[#2FA851]'; // CompleteButtonGreenColor
    case 'Unavailable':
      return 'text-[#FF2323]'; // UnavailableStatus
    case 'In Use (Full)':
      return 'text-[#2F80ED]'; // InUseStatus
    case 'Pending In Use':
      return 'text-[#E38C25]'; // MoveTaskBackgroundColor
    default:
      return 'text-[#445E71]';
  }
}

const statusI18nKeyMap: Record<string, string> = {
  Available: 'dockDoors.available',
  Unavailable: 'dockDoors.unavailable',
  'In Use (Full)': 'dockDoors.inUseFull',
  'Pending In Use': 'dockDoors.pendingInUse',
  Damaged: 'dockDoors.damaged',
};

// ── Empty State — Matches Xamarin EmptyListView with DockDoorsEmpty ──
function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-6 px-4 py-16">
      <img
        src="/images/dock_door.svg"
        alt=""
        className="h-32 w-32 sm:h-40 sm:w-40 opacity-80"
        aria-hidden="true"
      />
      <p className="text-lg sm:text-xl font-poppins font-bold text-[#445E71] text-center">
        {t('dockDoors.notFound')}
      </p>
    </div>
  );
}

export function DockDoorTable({
  locations,
  updatingLocation,
  hasPermission,
  onToggleStatus,
}: DockDoorTableProps) {
  const { t } = useTranslation();

  if (locations.length === 0) {
    return <EmptyState />;
  }

  return (
    // Outer Frame — Matches Xamarin: BorderColor=#A1CDE8, CornerRadius=10, Margin="10,5", Padding="1"
    <div className="mx-[10px] my-[5px] overflow-hidden rounded-[10px] border border-[#A1CDE8] bg-white">
      {/* ── Header Row — Matches Xamarin: bg #D7E7F3, Padding="20,5", HeightRequest=50 ── */}
      <div
        className={cn(
          'grid items-center bg-[#D7E7F3] px-5 py-[5px] min-h-[50px]',
          // Portrait (phone): 20*,30*,50* — Landscape (wider): 30*,30*,40*
          'grid-cols-[20fr_30fr_50fr] md:grid-cols-[30fr_30fr_40fr]'
        )}
      >
        <span className="text-xs sm:text-base md:text-lg font-poppins font-bold text-[#003366]">
          {t('dockDoors.location')}
        </span>
        <span className="text-xs sm:text-base md:text-lg font-poppins font-bold text-[#003366]">
          {t('dockDoors.status')}
        </span>
        <span className="text-xs sm:text-base md:text-lg font-poppins font-bold text-[#003366] text-center">
          {t('dockDoors.action')}
        </span>
      </div>

      {/* ── Data Rows ── */}
      <div className="bg-white">
        {locations.map((item, index) => {
          const displayStatus = normalizeStatus(item.LocStatus);

          return (
            <div key={item.Location}>
              {/* Separator — Matches Xamarin: HeightRequest=1(tablet)/3(phone), Margin="5,0,5,12", bg #C3D2DC */}
              {index > 0 && (
                <div className="mx-[5px] my-0 mb-[12px]">
                  <div className="h-[3px] md:h-[1px] bg-[#C3D2DC]" />
                </div>
              )}

              {/* Data Row — Matches Xamarin: RowSpacing=0, Margin="10,0", ColumnSpacing=5 */}
              <div
                className={cn(
                  'grid items-center gap-x-[5px] mx-[10px] py-[10px]',
                  index === 0 ? 'pt-[12px]' : '',
                  // Same responsive columns as header
                  'grid-cols-[20fr_30fr_50fr] md:grid-cols-[30fr_30fr_40fr]'
                )}
              >
                {/* Col 0: Location — Matches Xamarin "{Zone} - {Name}" FormattedString */}
                <span
                  className={cn(
                    'text-xs sm:text-base font-poppins text-[#445E71]',
                    'mt-[10px] leading-tight'
                  )}
                >
                  {item.Location}
                </span>

                {/* Col 1: Status — Matches Xamarin DataTrigger color mapping */}
                <span
                  className={cn(
                    'text-xs sm:text-base font-poppins font-medium mt-[10px]',
                    getStatusColor(item.LocStatus)
                  )}
                >
                  {statusI18nKeyMap[displayStatus]
                    ? t(statusI18nKeyMap[displayStatus])
                    : displayStatus}
                </span>

                {/* Col 2: Action — Toggle control or info message */}
                <div className="flex justify-center px-1">
                  <StatusToggleButton
                    status={item.LocStatus}
                    isLoading={updatingLocation === item.Location}
                    hasPermission={hasPermission}
                    onToggle={() => {
                      onToggleStatus(item);
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
